import React, { cloneElement, isValidElement, useCallback } from "react";

import { useScreenshotFormContext } from "../contexts/ScreenshotFormContext";
import { ScreenshotSettingProvider } from "../contexts/ScreenshotSettingContext";
import { useScreenshotCalculatedDefaults } from "../hooks";
import { SettingRow } from "./SettingRow";

interface ScreenshotSettingItemProps {
  label: React.ReactNode;
  name: string;
  valuePropName?: string;
  children?: React.ReactNode;
  colon?: boolean;
  labelWidth?: string;
  /**
   * Discrete controls (Switch, Select) commit immediately so their controlled
   * value updates without the typing debounce; text/number inputs stay debounced.
   */
  immediate?: boolean;
}

export default function ScreenshotSettingItem({
  name,
  valuePropName,
  label,
  children,
  colon,
  labelWidth,
  immediate
}: ScreenshotSettingItemProps) {
  const defaultSettings = useScreenshotCalculatedDefaults();
  const { values, onFieldChange, onFieldCommit } = useScreenshotFormContext();

  const state = {
    name,
    calculatedValue: defaultSettings[name]
  };

  const currentValue = values[name];

  const handleChange = useCallback(
    (value: unknown) => {
      // Booleans (Switches) are toggle-only — always commit immediately so the
      // controlled `checked` updates without the typing debounce, regardless of
      // how the control was authored. `immediate` additionally covers discrete
      // string controls like the font-family Select. Free text/number stays debounced.
      const commitNow = immediate || typeof value === "boolean";
      (commitNow ? onFieldCommit : onFieldChange)(name, value);
    },
    [immediate, name, onFieldChange, onFieldCommit]
  );

  const childPropName = valuePropName ?? "value";
  const childWithProps = isValidElement(children)
    ? cloneElement(children as React.ReactElement, {
        [childPropName]: currentValue,
        onChange: handleChange,
        id: name
      })
    : children;

  return (
    <ScreenshotSettingProvider state={state}>
      <SettingRow label={label} htmlFor={name} colon={colon} labelWidth={labelWidth}>
        {childWithProps}
      </SettingRow>
    </ScreenshotSettingProvider>
  );
}
